import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = {
  discrepancy_type: "", description: "", part: "",
  submitting_location: "", submitted_by: "", hold_case: true, accession: "",
};

/** Quick entry used from the case page and from Scan Mode at the receiving bench. */
export const RejectionDialog = ({ open, onOpenChange, caseId = "", accession = "", onLogged }) => {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY });
    api.get("/specimen-rejections", { params: { limit: 1 } })
      .then((r) => {
        setTypes(r.data.types || []);
        setForm((f) => ({ ...f, discrepancy_type: (r.data.types || [])[0] || "" }));
      })
      .catch(() => {});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/specimen-rejections", {
        ...form, case_id: caseId || null, accession_number: accession || form.accession.trim(),
      });
      toast.success(`${data.rejection_number} logged${data.holding ? " — case held until resolved" : ""}`);
      onOpenChange(false);
      onLogged?.(data);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="rejection-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            Log a specimen discrepancy{accession ? ` — ${accession}` : ""}
          </DialogTitle>
          <DialogDescription>
            Record it as received. The log number is assigned automatically and the resolution — who you
            called and what they confirmed — is documented when you close it.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {!caseId && !accession && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">
                Accession number (leave blank if it was never accessioned)
              </Label>
              <Input value={form.accession} placeholder="e.g. S26-03169"
                     data-testid="rejection-accession-input"
                     className="mt-1 h-11 border-slate-300 font-mono"
                     onChange={(e) => setForm((f) => ({ ...f, accession: e.target.value }))} />
              <p className="mt-1 text-xs text-slate-500">
                Enter the accession to attach this to an existing case and hold it from advancing.
              </p>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">What was wrong *</Label>
              <select value={form.discrepancy_type} required data-testid="rejection-type-select"
                      onChange={(e) => setForm((f) => ({ ...f, discrepancy_type: e.target.value }))}
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Container / part</Label>
              <Input value={form.part} placeholder="e.g. Part C, or 2 of 12"
                     data-testid="rejection-part-input" className="mt-1 h-11 border-slate-300"
                     onChange={(e) => setForm((f) => ({ ...f, part: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600">What happened *</Label>
            <Textarea value={form.description} required minLength={5} data-testid="rejection-description-input"
                      placeholder="e.g. Jar arrived with no patient label; requisition says right apex"
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      className="mt-1 min-h-[80px] border-slate-300" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Submitting location</Label>
              <Input value={form.submitting_location} data-testid="rejection-location-input"
                     placeholder="Clinic or floor" className="mt-1 h-11 border-slate-300"
                     onChange={(e) => setForm((f) => ({ ...f, submitting_location: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Submitted by</Label>
              <Input value={form.submitted_by} data-testid="rejection-submitter-input"
                     placeholder="Provider who sent it" className="mt-1 h-11 border-slate-300"
                     onChange={(e) => setForm((f) => ({ ...f, submitted_by: e.target.value }))} />
            </div>
          </div>
          <label className="flex items-start gap-2 border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <input type="checkbox" checked={form.hold_case} data-testid="rejection-hold-checkbox"
                   onChange={(e) => setForm((f) => ({ ...f, hold_case: e.target.checked }))}
                   className="mt-0.5 h-4 w-4" />
            <span>Hold this case from advancing until the discrepancy is resolved.</span>
          </label>
          <Button type="submit" disabled={busy} data-testid="rejection-submit"
                  className="h-11 w-full bg-red-800 hover:bg-red-900">
            {busy ? "Logging…" : "Log discrepancy"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RejectionDialog;
